import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import mammoth from 'mammoth';

function UploadSignDoc() {
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [fileId, setFileId] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [extractedFields, setExtractedFields] = useState([]);
  const [signDocFields, setSignDocsFields] = useState([
    { name: '', data_type: '', tag: '', required: true }
  ]);
  const [staticFields, setStaticFields] = useState([
    { name: 'Дата начала курса', tag: 'course_start_date', value: '' },
    { name: 'Дата окончания курса', tag: 'course_end_date', value: '' }
  ]);
  const { authFetch } = useAuth();
  const navigate = useNavigate();

  // Function to parse docx file and extract words between curly braces
  const parseDocxFile = async (file) => {
    try {
      // Use mammoth to extract text from docx
      const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
      const content = result.value;
      
      // Find all occurrences of words between curly braces
      const curlyBracesRegex = /\{([^}]+)\}/g;
      const matches = [];
      let match;
      
      while ((match = curlyBracesRegex.exec(content)) !== null) {
        const fieldName = match[1].trim();
        if (fieldName && !matches.includes(fieldName)) {
          matches.push(fieldName);
        }
      }
      
      console.log('Extracted text from docx:', content);
      console.log('Found placeholders:', matches);
      
      return matches;
    } catch (error) {
      console.error('Error parsing docx file:', error);
      return [];
    }
  };

  // Function to auto-populate fields from extracted placeholders
  const autoPopulateFields = (extractedPlaceholders) => {
    // Filter out specific tags that should not be auto-populated
    const filteredPlaceholders = extractedPlaceholders.filter(placeholder => {
      const excludedTags = ['full_name', 'first_name_fl', 'surname_fl', 'auto_doc_num', 'full_name_short', 'course_start_date', 'course_end_date'];
      return !excludedTags.includes(placeholder);
    });

    // Create fields from filtered placeholders
    const newFields = filteredPlaceholders.map(placeholder => ({
      name: "",
      data_type: 'text', // Default to text, user can change
      tag: placeholder,
      required: true
    }));

    // Add default fields that should always appear
    const defaultFields = [
      { name: 'Фамилия', data_type: 'text', tag: 'last_name', required: true },
      { name: 'Имя', data_type: 'text', tag: 'first_name', required: true },
      { name: 'Отчество', data_type: 'text', tag: 'surname', required: true }
    ];

    // Combine default fields with auto-populated fields
    setSignDocsFields([...defaultFields, ...newFields]);
  };

  const handleFieldChange = (index, field, value) => {
    const newFields = [...signDocFields];
    newFields[index] = { ...newFields[index], [field]: value };
    setSignDocsFields(newFields);
  };

  const addField = () => {
    setSignDocsFields([...signDocFields, { name: '', data_type: '', tag: '', required: true }]);
  };

  const removeField = (index) => {
    const newFields = signDocFields.filter((_, i) => i !== index);
    setSignDocsFields(newFields);
  };

  const addStaticField = () => {
    setStaticFields([...staticFields, { name: '', tag: '', value: '' }]);
  };

  const removeStaticField = (index) => {
    const newFields = staticFields.filter((_, i) => i !== index);
    setStaticFields(newFields);
  };

  const handleStaticFieldChange = (index, field, value) => {
    const newFields = [...staticFields];
    newFields[index] = { ...newFields[index], [field]: value };
    setStaticFields(newFields);
  };

  // Function to validate field
  const isFieldValid = (field) => {
    const basicValid = field.name.trim() !== '' && field.tag.trim() !== '';
    if (field.data_type === 'enum') {
      return basicValid && field.params && field.params.enum_options && 
             field.params.enum_options.length > 0 && 
             field.params.enum_options.every(option => option.trim() !== '');
    }
    return basicValid;
  };

  // Function to validate static field
  const isStaticFieldValid = (field) => {
    return field.name.trim() !== '' && field.tag.trim() !== '' && field.value.trim() !== '';
  };

  // Function to get field validation status
  const getFieldValidationStatus = (field) => {
    const isNameEmpty = field.name.trim() === '';
    const isTagEmpty = field.tag.trim() === '';
    const isEnumOptionsEmpty = field.data_type === 'enum' && 
      (!field.params || !field.params.enum_options || field.params.enum_options.length === 0 || 
       field.params.enum_options.some(option => option.trim() === ''));
    
    if (isNameEmpty || isTagEmpty || isEnumOptionsEmpty) {
      const emptyFields = [];
      if (isNameEmpty) emptyFields.push('Название поля');
      if (isTagEmpty) emptyFields.push('Тег');
      if (isEnumOptionsEmpty) emptyFields.push('Варианты перечисления');
      
      return {
        isValid: false,
        message: `${emptyFields.join(', ')} ${emptyFields.length > 1 ? 'обязательны' : 'обязательно'}`
      };
    }
    return { isValid: true, message: '' };
  };

  // Function to get static field validation status
  const getStaticFieldValidationStatus = (field) => {
    const isNameEmpty = field.name.trim() === '';
    const isTagEmpty = field.tag.trim() === '';
    const isValueEmpty = field.value.trim() === '';
    
    if (isNameEmpty || isTagEmpty || isValueEmpty) {
      const emptyFields = [];
      if (isNameEmpty) emptyFields.push('Название поля');
      if (isTagEmpty) emptyFields.push('Тег');
      if (isValueEmpty) emptyFields.push('Значение');
      
      return {
        isValid: false,
        message: `${emptyFields.join(', ')} ${emptyFields.length > 1 ? 'обязательны' : 'обязательно'}`
      };
    }
    return { isValid: true, message: '' };
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    console.log(selectedFile);

    setFile(selectedFile);
    setUploadStatus('uploading');

    // Parse the docx file to extract placeholders
    const placeholders = await parseDocxFile(selectedFile);
    setExtractedFields(placeholders);
    console.log('Extracted placeholders:', placeholders);

    const formData = new FormData();
    formData.append('file', selectedFile);
    
    // Debug logging
    console.log('FormData contents:');
    for (let pair of formData.entries()) {
      console.log(pair[0], pair[1]);
    }

    try {
      const response = await authFetch('/api/v1/file-upload', {
        method: 'POST',
        //headers: {
        //  'Content-Type': "multipart/form-data"
        //},
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setFileId(data.file_id);
        setUploadStatus('success');
        
        // Auto-populate fields if placeholders were found
        if (placeholders.length > 0) {
          autoPopulateFields(placeholders);
        }
      } else {
        setUploadStatus('error');
        console.error('File upload failed');
      }
    } catch (error) {
      setUploadStatus('error');
      console.error('Error uploading file:', error);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!fileId) {
      console.error('No file ID available');
      return;
    }

    // Validate all fields before submission
    const invalidFields = signDocFields.filter(field => !isFieldValid(field));
    if (invalidFields.length > 0) {
      console.error('Some fields are invalid:', invalidFields);
      return;
    }

    // Validate static fields before submission
    const invalidStaticFields = staticFields.filter(field => !isStaticFieldValid(field));
    if (invalidStaticFields.length > 0) {
      console.error('Some static fields are invalid:', invalidStaticFields);
      return;
    }

    try {
      const response = await authFetch('/api/v1/template-docs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          file_id: fileId.id,
          sign_doc_fields: signDocFields,
          static_fields: staticFields
        })
      });

      if (response.ok) {
        console.log('Document uploaded successfully');
        // Clear form
        setName('');
        setFile(null);
        setFileId('');
        setUploadStatus('');
        setSignDocsFields([{ name: '', data_type: '', tag: '', required: true }]);
        setStaticFields([
          { name: 'Дата начала курса', tag: 'course_start_date', value: '' },
          { name: 'Дата окончания курса', tag: 'course_end_date', value: '' },
          { name: 'Номер документа', tag: 'auto_doc_num', value: '' }
        ]);
        navigate('/template-docs'); // Redirect to template list
      } else {
        console.error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
    }
  };

  return (
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card">
            <div className="card-body">
              <h2 className="card-title text-center mb-4">Загрузка документа для подписи</h2>
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="file" className="form-label">Файл документа</label>
                  <input
                    type="file"
                    className="form-control"
                    id="file"
                    onChange={handleFileChange}
                    required
                  />
                  {uploadStatus === 'uploading' && (
                    <div className="text-info mt-2">Загрузка файла...</div>
                  )}
                  {uploadStatus === 'success' && (
                    <div className="text-success mt-2">Файл успешно загружен</div>
                  )}
                  {uploadStatus === 'error' && (
                    <div className="text-danger mt-2">Ошибка при загрузке файла</div>
                  )}
                </div>

                {/* Display extracted placeholders */}
                {extractedFields.length > 0 && uploadStatus === 'success' && (
                  <div className="mb-3">
                    <div className="alert alert-info">
                      <h6 className="alert-heading">Найденные поля в документе:</h6>
                      <div className="mt-2">
                        {extractedFields.map((field, index) => (
                          <span key={index} className="badge bg-primary me-2 mb-1">
                            {field}
                          </span>
                        ))}
                      </div>
                      <small className="text-muted">
                        Эти поля были автоматически добавлены в форму ниже. Вы можете их редактировать или удалить.
                      </small>
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <label htmlFor="name" className="form-label">Название документа</label>
                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={uploadStatus !== 'success'}
                  />
                </div>

                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label mb-0 fw-bold">Поля документа</label>
                    <button 
                      type="button" 
                      className="btn btn-outline-primary btn-sm"
                      onClick={addField}
                      disabled={uploadStatus !== 'success'}
                    >
                      Добавить поле
                    </button>
                  </div>
                  
                  {signDocFields.map((field, index) => {
                    const validationStatus = getFieldValidationStatus(field);
                    return (
                      <div key={index} className={`card mb-2 ${!validationStatus.isValid ? 'border-warning' : ''}`}>
                        <div className={`card-body ${!validationStatus.isValid ? 'bg-warning bg-opacity-10' : ''}`}>
                          <div className="d-flex justify-content-between mb-2">
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => removeField(index)}
                              disabled={uploadStatus !== 'success'}
                            >
                              Удалить
                            </button>
                          </div>
                          <div className="row g-3">
                            <div className="col-md-6">
                              <input
                                type="text"
                                className={`form-control ${field.name.trim() === '' ? 'border-warning' : ''}`}
                                placeholder="Название поля"
                                value={field.name}
                                onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                                disabled={uploadStatus !== 'success'}
                              />
                            </div>
                            <div className="col-md-6">
                              <select
                                className="form-select"
                                value={field.data_type}
                                onChange={(e) => handleFieldChange(index, 'data_type', e.target.value)}
                                disabled={uploadStatus !== 'success'}
                              >
                                <option value="">Выберите тип данных</option>
                                <option value="text">Текст</option>
                                <option value="date">Дата</option>
                                <option value="integer">Целое число</option>
                                <option value="enum">Перечисление</option>
                              </select>
                            </div>
                            <div className="col-md-6">
                              <input
                                type="text"
                                className={`form-control ${field.tag.trim() === '' ? 'border-warning' : ''}`}
                                placeholder="Тег"
                                value={field.tag}
                                onChange={(e) => handleFieldChange(index, 'tag', e.target.value)}
                                disabled={uploadStatus !== 'success'}
                              />
                            </div>
                            {field.data_type === 'enum' && (
                              <div className="col-md-12">
                                <div className="mb-2">
                                  <label className="form-label fw-bold">Варианты перечисления:</label>
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm ms-2"
                                    onClick={() => {
                                      const currentParams = field.params || {};
                                      const currentOptions = currentParams.enum_options || [];
                                      const newOptions = [...currentOptions, ''];
                                      handleFieldChange(index, 'params', { ...currentParams, enum_options: newOptions });
                                    }}
                                    disabled={uploadStatus !== 'success'}
                                  >
                                    Добавить вариант
                                  </button>
                                </div>
                                {((field.params || {}).enum_options || []).map((option, optionIndex) => (
                                  <div key={optionIndex} className="d-flex align-items-center mb-2">
                                    <input
                                      type="text"
                                      className="form-control me-2"
                                      placeholder={`Вариант ${optionIndex + 1}`}
                                      value={option}
                                      onChange={(e) => {
                                        const currentParams = field.params || {};
                                        const currentOptions = currentParams.enum_options || [];
                                        const newOptions = [...currentOptions];
                                        newOptions[optionIndex] = e.target.value;
                                        handleFieldChange(index, 'params', { ...currentParams, enum_options: newOptions });
                                      }}
                                      disabled={uploadStatus !== 'success'}
                                    />
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={() => {
                                        const currentParams = field.params || {};
                                        const currentOptions = currentParams.enum_options || [];
                                        const newOptions = currentOptions.filter((_, i) => i !== optionIndex);
                                        handleFieldChange(index, 'params', { ...currentParams, enum_options: newOptions });
                                      }}
                                      disabled={uploadStatus !== 'success'}
                                    >
                                      Удалить
                                    </button>
                                  </div>
                                ))}
                                {(!field.params || !field.params.enum_options || field.params.enum_options.length === 0) && (
                                  <small className="text-muted">
                                    Нажмите "Добавить вариант" чтобы создать варианты перечисления
                                  </small>
                                )}
                              </div>
                            )}
                            <div className="col-md-6">
                              <div className="form-check mt-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`required-${index}`}
                                  checked={field.required}
                                  onChange={(e) => handleFieldChange(index, 'required', e.target.checked)}
                                  disabled={uploadStatus !== 'success'}
                                />
                                <label className="form-check-label" htmlFor={`required-${index}`}>
                                  Обязательное поле
                                </label>
                              </div>
                            </div>
                          </div>
                          {!validationStatus.isValid && (
                            <div className="mt-2">
                              <small className="text-warning">
                                <i className="bi bi-exclamation-triangle me-1"></i>
                                {validationStatus.message}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label mb-0 fw-bold">Статические поля (общие для всего курса)</label>
                    <button 
                      type="button" 
                      className="btn btn-outline-secondary btn-sm"
                      onClick={addStaticField}
                      disabled={uploadStatus !== 'success'}
                    >
                      Добавить статическое поле
                    </button>
                  </div>
                  
                  {staticFields.map((field, index) => {
                    const validationStatus = getStaticFieldValidationStatus(field);
                    return (
                      <div key={index} className={`card mb-2 ${!validationStatus.isValid ? 'border-warning' : ''}`}>
                        <div className={`card-body ${!validationStatus.isValid ? 'bg-warning bg-opacity-10' : ''}`}>
                          <div className="d-flex justify-content-between mb-2">
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => removeStaticField(index)}
                              disabled={uploadStatus !== 'success'}
                            >
                              Удалить
                            </button>
                          </div>
                          <div className="row g-3">
                            <div className="col-md-4">
                              <input
                                type="text"
                                className={`form-control ${field.name.trim() === '' ? 'border-warning' : ''}`}
                                placeholder="Название поля"
                                value={field.name}
                                onChange={(e) => handleStaticFieldChange(index, 'name', e.target.value)}
                                disabled={uploadStatus !== 'success'}
                              />
                            </div>
                            <div className="col-md-4">
                              <input
                                type="text"
                                className={`form-control ${field.tag.trim() === '' ? 'border-warning' : ''}`}
                                placeholder="Тег"
                                value={field.tag}
                                onChange={(e) => handleStaticFieldChange(index, 'tag', e.target.value)}
                                disabled={uploadStatus !== 'success'}
                              />
                            </div>
                            <div className="col-md-4">
                              <input
                                type="text"
                                className={`form-control ${field.value.trim() === '' ? 'border-warning' : ''}`}
                                placeholder="Значение"
                                value={field.value}
                                onChange={(e) => handleStaticFieldChange(index, 'value', e.target.value)}
                                disabled={uploadStatus !== 'success'}
                              />
                            </div>
                          </div>
                          {!validationStatus.isValid && (
                            <div className="mt-2">
                              <small className="text-warning">
                                <i className="bi bi-exclamation-triangle me-1"></i>
                                {validationStatus.message}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary w-100"
                  disabled={uploadStatus !== 'success'}
                >
                  Загрузить
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UploadSignDoc; 